import { useState } from 'react'
import { Bot, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useAgentTasks, usePromptAgent } from '../hooks/useSupport'

const labels = {
  running: 'Running',
  waiting: 'Waiting for publishing',
  completed: 'Completed',
  needs_attention: 'Needs attention',
}

export function AgentPromptComposer({ ticketId }: { ticketId: string }) {
  const [prompt, setPrompt] = useState('')
  const [parentTaskId, setParentTaskId] = useState<string | null>(null)
  const mutation = usePromptAgent()
  const tasksQuery = useAgentTasks(ticketId)
  const tasks = tasksQuery.data || []
  const running = mutation.isPending || tasks.some((task) => task.status === 'running')
  const visible = tasks.filter(
    (task, index) => task.status !== 'completed' || index >= tasks.length - 5
  )
  const parent = tasks.find((task) => task.id === parentTaskId)

  return (
    <Card className="border-violet-200 dark:border-violet-900">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bot className="h-4 w-4 text-violet-500" /> Ask the agent
        </CardTitle>
        <CardDescription>
          Check pending artworks or approve them for publishing. Instructions and follow-ups stay on
          this ticket. Publishing is checked before a task is marked completed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {tasksQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Loading agent tasks…</p>
        )}
        {tasksQuery.isError && (
          <p role="alert" className="text-sm text-destructive">
            Could not load task status. Refresh to check existing instructions.
          </p>
        )}
        {visible.map((task) => (
          <div key={task.id} className="rounded-md bg-muted/50 p-3 text-sm space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={task.status === 'needs_attention' ? 'destructive' : 'secondary'}>
                {labels[task.status]}
              </Badge>
              {task.parentTaskId && (
                <span className="text-xs text-muted-foreground">Follow-up</span>
              )}
            </div>
            <p className="font-medium whitespace-pre-wrap">{task.prompt}</p>
            {task.response && <p className="whitespace-pre-wrap">{task.response}</p>}
            {task.outcomes.map((outcome) => (
              <p
                key={outcome.id}
                className={
                  outcome.publishingStatus === 'completed'
                    ? 'text-green-700 dark:text-green-400'
                    : !outcome.success || outcome.publishingStatus === 'needs_attention'
                      ? 'text-destructive'
                      : ''
                }
              >
                {outcome.name} (#{outcome.id}):{' '}
                {outcome.publishingStatus === 'completed'
                  ? 'Published · verified'
                  : outcome.error ||
                    (outcome.success ? 'Approved · waiting for publishing' : 'Approval failed')}
              </p>
            ))}
            {task.errorMessage && <p className="text-destructive">{task.errorMessage}</p>}
            {task.outcomes.some((outcome) => outcome.taskId) && (
              <a href="/app/tasks" className="text-xs underline">
                View publishing jobs
              </a>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setParentTaskId(task.id)}
            >
              Follow up
            </Button>
          </div>
        ))}
        <form
          className="space-y-3"
          onSubmit={async (event) => {
            event.preventDefault()
            if (!prompt.trim() || running) return
            try {
              await mutation.mutateAsync({ ticketId, prompt: prompt.trim(), parentTaskId })
              setPrompt('')
              setParentTaskId(null)
            } catch {
              // Preserve the instruction so it can be edited after inspecting the task.
            }
          }}
        >
          {parent && (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <span>Following up: {parent.prompt}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setParentTaskId(null)}>
                Cancel
              </Button>
            </div>
          )}
          <label htmlFor={`agent-prompt-${ticketId}`} className="text-sm font-medium">
            Instruction for the agent
          </label>
          <Textarea
            id={`agent-prompt-${ticketId}`}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Approve the artworks mentioned in this conversation."
            maxLength={4000}
            rows={3}
            disabled={running}
          />
          <div className="flex justify-between items-center gap-3">
            <span className="text-xs text-muted-foreground">
              Internal only · approvals take effect when run
            </span>
            <Button
              type="submit"
              size="sm"
              disabled={!prompt.trim() || running || tasksQuery.isError || tasksQuery.isLoading}
            >
              {running && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {running ? 'Running…' : parentTaskId ? 'Send follow-up' : 'Run agent'}
            </Button>
          </div>
          <div role="status" aria-live="polite" className="text-sm">
            {running &&
              'The agent is carrying out an instruction. You can return to this ticket to check progress.'}
            {mutation.isError && (
              <span className="text-destructive">
                The request could not finish. Review the saved task and audit trail before retrying.
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
